import PropTypes from 'prop-types';
import { usePermission } from '../../hooks/usePermission';

/**
 * PermissionGate - Conditional rendering based on permissions
 * 
 * Phase 5: M6.T6.2
 * 
 * Features:
 * - Hide/show content based on permission
 * - Support for single or multiple permissions
 * - Optional fallback content
 * - OR logic (hasAny) or AND logic (hasAll)
 * 
 * Usage Examples:
 * 
 * // Hide button without permission
 * <PermissionGate permission="users.create">
 *   <Button onClick={handleCreate}>Kullanıcı Ekle</Button>
 * </PermissionGate>
 * 
 * // Show fallback message
 * <PermissionGate
 *   permission="users.view"
 *   fallback={<Alert severity="warning">Yetkiniz yok</Alert>}
 * >
 *   <UsersTable />
 * </PermissionGate>
 * 
 * // Require ANY permission (OR logic)
 * <PermissionGate permissions={["users.update", "users.delete"]}>
 *   <IconButton><EditIcon /></IconButton>
 * </PermissionGate>
 * 
 * // Require ALL permissions (AND logic)
 * <PermissionGate
 *   permissions={["users.view", "users.update"]}
 *   requireAll={true}
 * >
 *   <AdminPanel />
 * </PermissionGate>
 */
const PermissionGate = ({
  permission,
  permissions,
  requireAll = false,
  children,
  fallback = null
}) => {
  const { hasPermission, hasAny, hasAll } = usePermission();
  
  let hasAccess = false;
  
  // Single permission check
  if (permission) {
    hasAccess = hasPermission(permission);
  }
  // Multiple permissions check
  else if (permissions && permissions.length > 0) {
    hasAccess = requireAll ? hasAll(permissions) : hasAny(permissions);
  }
  // No permission specified → deny access
  else {
    hasAccess = false;
  }
  
  // Render children if has access, otherwise render fallback
  return hasAccess ? children : fallback;
};

PermissionGate.propTypes = {
  // Single permission (e.g., "users.view")
  permission: PropTypes.string,
  
  // Multiple permissions (e.g., ["users.view", "users.create"])
  permissions: PropTypes.arrayOf(PropTypes.string),
  
  // If true, require ALL permissions (AND logic)
  // If false, require ANY permission (OR logic)
  requireAll: PropTypes.bool,
  
  // Content to render if has permission
  children: PropTypes.node.isRequired,
  
  // Content to render if no permission (optional)
  fallback: PropTypes.node
};

export default PermissionGate;

